import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ManualAdCarousel } from './manual-ad-carousel';

// Tag color palette - same family, different shades
const tagColors = [
  { bg: '#E8F4F4', text: '#3D7A7A' },  // Teal
  { bg: '#EFF6FF', text: '#1E40AF' },  // Blue
  { bg: '#FEF3C7', text: '#92400E' },  // Amber
  { bg: '#ECFDF5', text: '#047857' },  // Emerald
  { bg: '#FDF2F8', text: '#BE185D' },  // Pink
  { bg: '#F3E8FF', text: '#7C3AED' },  // Purple
  { bg: '#FFF7ED', text: '#C2410C' },  // Orange
  { bg: '#F0FDF4', text: '#166534' },  // Green
  { bg: '#EFF6FF', text: '#1D4ED8' },  // Indigo
  { bg: '#FFFBEB', text: '#B45309' },  // Yellow
  { bg: '#F5F3FF', text: '#6D28D9' },  // Violet
  { bg: '#ECFEFF', text: '#0E7490' },  // Cyan
];

// Default sample tags if no data from admin
const defaultFieldTags = [
  { name: 'Business & Management', href: '/scholarships?field=business' },
  { name: 'Engineering', href: '/scholarships?field=engineering' },
  { name: 'Medicine & Health', href: '/scholarships?field=medicine' },
  { name: 'Computer Science', href: '/scholarships?field=computer-science' },
  { name: 'Arts & Design', href: '/scholarships?field=arts' },
  { name: 'Law', href: '/scholarships?field=law' },
  { name: 'Science', href: '/scholarships?field=science' },
  { name: 'Education', href: '/scholarships?field=education' },
  { name: 'Hospitality', href: '/scholarships?field=hospitality' },
  { name: 'Finance', href: '/scholarships?field=finance' },
  { name: 'Architecture', href: '/scholarships?field=architecture' },
];

const defaultUniversityTags = [
  { name: 'Harvard University', href: '/scholarships?university=harvard' },
  { name: 'MIT', href: '/scholarships?university=mit' },
  { name: 'Stanford', href: '/scholarships?university=stanford' },
  { name: 'Oxford', href: '/scholarships?university=oxford' },
  { name: 'Cambridge', href: '/scholarships?university=cambridge' },
  { name: 'Yale', href: '/scholarships?university=yale' },
  { name: 'Princeton', href: '/scholarships?university=princeton' },
  { name: 'Columbia', href: '/scholarships?university=columbia' },
  { name: 'Berkeley', href: '/scholarships?university=berkeley' },
  { name: 'UCLA', href: '/scholarships?university=ucla' },
  { name: 'NYU', href: '/scholarships?university=nyu' },
];

export const ScholarshipTagDiscovery = ({ title, description, courseFields = [], universities = [], adBannerItems = [] }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('course');
  
  // Use provided tags or default sample tags
  const fieldTags = courseFields.length > 0 ? courseFields : defaultFieldTags;
  const universityTags = universities.length > 0 ? universities : defaultUniversityTags;
  
  const currentTags = activeTab === 'course' ? fieldTags : universityTags;
  const MAX_VISIBLE_TAGS = 11; // 11 tags + 1 "+more" = 12 total
  const visibleTags = currentTags.slice(0, MAX_VISIBLE_TAGS);
  const hasMoreTags = currentTags.length > MAX_VISIBLE_TAGS;

  const defaultAdItems = [
    { imageUrl: '/placeholders/image-generic.svg', clickUrl: '#', alt: 'Scholarship Ad 1' },
    { imageUrl: '/placeholders/image-generic.svg', clickUrl: '#', alt: 'Scholarship Ad 2' },
  ];

  const ads = adBannerItems.length > 0 ? adBannerItems : defaultAdItems;

  return (
    <div data-testid="scholarship-tag-discovery" className="text-center py-16">
      {title && <h2 className="text-[1.6rem] md:text-3xl lg:text-4xl text-[#00224F] mb-3" style={{ fontWeight: 750 }} dangerouslySetInnerHTML={{ __html: title }}></h2>}
      {description && <div className="text-[#5A6978] mb-10 max-w-2xl mx-auto text-sm md:text-base" dangerouslySetInnerHTML={{ __html: description }}></div>}

      <div className="inline-flex items-center p-1 bg-[#F5F3F0] rounded-lg mb-8">
        <button
          onClick={() => setActiveTab('course')}
          className={`flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 text-xs md:text-sm font-medium transition-all duration-200 rounded-md ${activeTab === 'course' ? 'bg-[#00224F] text-white' : 'text-[#5A6978] hover:text-[#00224F]'}`}
          data-testid="tab-by-course"
        >
          Quickview by Course
          <svg className="w-4 h-4 hidden md:block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
        </button>
        <button
          onClick={() => setActiveTab('university')}
          className={`flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 text-xs md:text-sm font-medium transition-all duration-200 rounded-md ${activeTab === 'university' ? 'bg-[#00224F] text-white' : 'text-[#5A6978] hover:text-[#00224F]'}`}
          data-testid="tab-by-university"
        >
          Quickview by University
          <svg className="w-4 h-4 hidden md:block" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
        </button>
      </div>

      {/* Tags Section - 2-3 rows arrangement */}
      <div className="max-w-3xl mx-auto mb-14">
        <div className="flex flex-wrap justify-center gap-3">
          {visibleTags.map((tag, index) => {
            const colorIndex = index % tagColors.length;
            const color = tagColors[colorIndex];
            return (
              <a
                key={index}
                href={tag.href}
                className="inline-flex items-center px-4 py-2.5 rounded-full hover:shadow-md hover:scale-105 transition-all duration-200 text-sm font-medium"
                style={{ backgroundColor: color.bg, color: color.text }}
                data-testid="scholarship-tag"
              >
                {tag.name}
              </a>
            );
          })}
          {/* Always show +more button */}
          <button
            onClick={() => navigate('/scholarships')}
            className="inline-flex items-center px-4 py-2.5 rounded-full bg-[#F97316] text-white hover:bg-[#EA580C] hover:shadow-md hover:scale-105 transition-all duration-200 text-sm font-medium"
            data-testid="scholarship-more-tag"
          >
            +more
          </button>
        </div>
      </div>

      {/* Ad Banner Carousel */}
      <div className="max-w-[1320px] mx-auto">
        <ManualAdCarousel items={ads} />
      </div>
    </div>
  );
};
